// src/components/settings/CargoIntegrationSettings.jsx
// Component for managing cargo company integration settings

import React, { useState, useEffect } from 'react';
import cargoService from '../../services/cargoService';

const styles = {
  container: {
    background: 'white',
    padding: '24px',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    marginBottom: '24px'
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '20px'
  },
  formGroup: {
    marginBottom: '16px'
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '500',
    color: '#374151'
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    fontSize: '14px',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    boxSizing: 'border-box'
  },
  select: {
    width: '100%',
    padding: '8px 12px',
    fontSize: '14px',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    background: 'white'
  },
  button: {
    padding: '8px 16px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500'
  },
  buttonSuccess: {
    backgroundColor: '#10b981'
  },
  buttonDanger: {
    backgroundColor: '#ef4444'
  },
  buttonSecondary: {
    backgroundColor: '#6b7280'
  },
  credentialsList: {
    marginTop: '20px'
  },
  credentialItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    borderBottom: '1px solid #e5e7eb'
  },
  statusIndicator: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    display: 'inline-block',
    marginRight: '8px'
  },
  statusActive: {
    backgroundColor: '#10b981'
  },
  statusInactive: {
    backgroundColor: '#ef4444'
  },
  actions: {
    display: 'flex',
    gap: '8px'
  },
  actionButton: {
    padding: '4px 8px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px'
  },
  note: {
    fontSize: '12px',
    color: '#6b7280',
    fontStyle: 'italic',
    marginTop: '4px'
  }
};

const CargoIntegrationSettings = () => {
  const [cargoCompanies, setCargoCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [credentials, setCredentials] = useState({});
  const [savedCredentials, setSavedCredentials] = useState([]);
  const [validationResult, setValidationResult] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);

  // Load supported cargo companies
  useEffect(() => {
    const companies = cargoService.getSupportedCargoCompanies();
    setCargoCompanies(companies);
    
    // Load saved credentials from localStorage (in a real app, this would come from backend)
    const saved = JSON.parse(localStorage.getItem('cargoCredentials') || '[]');
    setSavedCredentials(saved);
  }, []);

  // Handle credential input changes
  const handleCredentialChange = (field, value) => {
    setCredentials(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Save credentials
  const handleSaveCredentials = async () => {
    if (!selectedCompany) {
      alert('Lütfen bir kargo firması seçin');
      return;
    }

    // Check if all required fields are filled
    const company = cargoCompanies.find(c => c.name === selectedCompany);
    if (company) {
      for (const field of company.requiredFields) {
        if (!credentials[field] || credentials[field].trim() === '') {
          alert(`${field} alanı boş bırakılamaz`);
          return;
        }
      }
    }

    setIsSaving(true);
    
    // Create credential object
    const credentialData = {
      id: Date.now(),
      company: selectedCompany,
      ...credentials,
      createdAt: new Date().toISOString()
    };

    // Update saved credentials
    const updatedCredentials = [
      ...savedCredentials.filter(c => c.company !== selectedCompany),
      credentialData
    ];
    
    setSavedCredentials(updatedCredentials);
    localStorage.setItem('cargoCredentials', JSON.stringify(updatedCredentials));
    
    setIsSaving(false);
    alert('Kargo entegrasyon bilgileri başarıyla kaydedildi!');
  };

  // Test credentials
  const handleTestCredentials = async () => {
    if (!selectedCompany) {
      alert('Lütfen bir kargo firması seçin');
      return;
    }

    // Check if all required fields are filled
    const company = cargoCompanies.find(c => c.name === selectedCompany);
    if (company) {
      for (const field of company.requiredFields) {
        if (!credentials[field] || credentials[field].trim() === '') {
          alert(`${field} alanı boş bırakılamaz`);
          return;
        }
      }
    }

    setIsTesting(true);
    setValidationResult(null);

    try {
      const result = await cargoService.validateCredentials(selectedCompany, credentials);
      setValidationResult(result);
      
      if (result.valid) {
        alert('Bağlantı testi başarılı!');
      } else {
        alert('Bağlantı testi başarısız: ' + result.message);
      }
    } catch (error) {
      const errorMessage = error.message || 'Bağlantı testi sırasında bir hata oluştu';
      setValidationResult({
        valid: false,
        message: errorMessage
      });
      alert('Bağlantı testi başarısız: ' + errorMessage);
    } finally {
      setIsTesting(false);
    }
  };

  // Delete credentials
  const handleDeleteCredentials = (companyId) => {
    if (window.confirm('Bu kargo entegrasyon bilgilerini silmek istediğinizden emin misiniz?')) {
      const updatedCredentials = savedCredentials.filter(c => c.id !== companyId);
      setSavedCredentials(updatedCredentials);
      localStorage.setItem('cargoCredentials', JSON.stringify(updatedCredentials));
      alert('Kargo entegrasyon bilgileri silindi');
    }
  };

  // Load credentials for editing
  const handleEditCredentials = (credential) => {
    setSelectedCompany(credential.company);
    const { id, company, createdAt, ...creds } = credential;
    setCredentials(creds);
  };

  // Render credential input fields based on selected company
  const renderCredentialFields = () => {
    if (!selectedCompany) return null;

    const company = cargoCompanies.find(c => c.name === selectedCompany);
    if (!company) return null;

    return (
      <div>
        <h4>{company.name} için Entegrasyon Bilgileri</h4>
        {company.requiredFields.map(field => (
          <div key={field} style={styles.formGroup}>
            <label style={styles.label}>
              {field === 'customerNumber' 
                ? 'Kargo Müşteri Numarası' 
                : field.charAt(0).toUpperCase() + field.slice(1)}
            </label>
            <input
              type="text"
              style={styles.input}
              value={credentials[field] || ''}
              onChange={(e) => handleCredentialChange(field, e.target.value)}
              placeholder={
                field === 'customerNumber' 
                  ? 'Kargo Müşteri Numarası girin' 
                  : `${field.charAt(0).toUpperCase() + field.slice(1)} girin`
              }
            />
            {field === 'customerNumber' && (
              <div style={styles.note}>
                Bu numara kargo firmasından alınan sözleşme kapsamında verilir
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Kargo Entegrasyon Ayarları</h2>
      <p style={{ color: '#6b7280', marginBottom: '20px' }}>
        Kargo etiketi oluşturmak için kargo firmalarınızın API bilgilerini buraya girin.
      </p>
      
      <div style={styles.formGroup}>
        <label style={styles.label}>Kargo Firması</label>
        <select
          style={styles.select}
          value={selectedCompany}
          onChange={(e) => setSelectedCompany(e.target.value)}
        >
          <option value="">Kargo Firması Seçin</option>
          {cargoCompanies.map(company => (
            <option key={company.name} value={company.name}>{company.name}</option>
          ))}
        </select>
      </div>

      {renderCredentialFields()}

      {selectedCompany && (
        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
          <button
            style={{...styles.button, ...(validationResult?.valid ? styles.buttonSuccess : {})}}
            onClick={handleTestCredentials}
            disabled={isTesting}
          >
            {isTesting ? 'Test Ediliyor...' : 'Bağlantıyı Test Et'}
          </button>
          <button
            style={{...styles.button, ...styles.buttonSecondary}}
            onClick={handleSaveCredentials}
            disabled={isSaving}
          >
            {isSaving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      )}

      {validationResult && (
        <div style={{
          marginTop: '15px',
          padding: '10px',
          borderRadius: '4px',
          backgroundColor: validationResult.valid ? '#d1fae5' : '#fee2e2',
          color: validationResult.valid ? '#065f46' : '#991b1b'
        }}>
          {validationResult.message}
        </div>
      )}

      {savedCredentials.length > 0 && (
        <div style={styles.credentialsList}>
          <h3>Kayıtlı Entegrasyonlar</h3>
          {savedCredentials.map(credential => (
            <div key={credential.id} style={styles.credentialItem}>
              <div>
                <span style={{...styles.statusIndicator, ...(credential.id ? styles.statusActive : styles.statusInactive)}}></span>
                <strong>{credential.company}</strong>
              </div>
              <div style={styles.actions}>
                <button
                  style={{...styles.actionButton, backgroundColor: '#f59e0b', color: 'white'}}
                  onClick={() => handleEditCredentials(credential)}
                >
                  Düzenle
                </button>
                <button
                  style={{...styles.actionButton, backgroundColor: '#ef4444', color: 'white'}}
                  onClick={() => handleDeleteCredentials(credential.id)}
                >
                  Sil
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      <div style={{ 
        marginTop: '20px', 
        padding: '15px', 
        backgroundColor: '#eff6ff', 
        borderRadius: '4px',
        border: '1px solid #dbeafe'
      }}>
        <h4 style={{ margin: '0 0 10px 0', color: '#1e40af' }}>Nasıl kullanılır?</h4>
        <ol style={{ margin: 0, paddingLeft: '20px', color: '#1e3a8a', fontSize: '14px' }}>
          <li>Bir kargo firması seçin</li>
          <li>Gerekli kimlik bilgilerini girin</li>
          <li>"Bağlantıyı Test Et" butonu ile bilgileri doğrulayın</li>
          <li>"Kaydet" butonu ile bilgileri saklayın</li>
          <li>Sipariş detay sayfasından kargo etiketi oluşturabilirsiniz</li>
        </ol>
      </div>
    </div>
  );
};

export default CargoIntegrationSettings;